import { Settings } from 'lucide-react';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { SettingsMenu, SettingsSection } from './settings/SettingsMenu';
import { ConnectionsSettingsTab } from './settings/ConnectionsSettingsTab';
import { SessionsSettingsTab } from './settings/SessionsSettingsTab';
import { UISettingsTab } from './settings/UISettingsTab';
import { SnippetsSettingsTab } from './settings/SnippetsSettingsTab';
import { MetaInfoSettingsTab } from './settings/MetaInfoSettingsTab';
import { AdvancedSettingsTab } from './settings/AdvancedSettingsTab';
import { BetaFeaturesSettingsTab } from './settings/BetaFeaturesSettingsTab';
import { LicenseSettingsTab } from './settings/LicenseSettingsTab';
import { AiTerminalSettingsTab } from './settings/AiTerminalSettingsTab';
import { AiIntegrationsSettingsTab } from './settings/AiIntegrationsSettingsTab';
import { UpdatesSettingsTab } from './settings/UpdatesSettingsTab';
import { PrivacySettingsTab } from './settings/PrivacySettingsTab';
import { SpatialSettingsTab } from './settings/SpatialSettingsTab';
import { BackupTransferSettingsTab } from '../../settings-transfer/components/BackupTransferSettingsTab';
import { KeyboardShortcutsSettingsTab } from '../../keybindings/components/KeyboardShortcutsSettingsTab';
import { WhatsNewSettingsTab } from '../../../features/whats-new';
import { useSettingsDialogStore } from '../store/settingsDialogStore';
import { AzureStorageSettingsSection } from './settings/AzureStorageSettingsSection';

interface SettingsDialogProps {
  onAddNewConnection: () => void;
}

const sectionTitles: Record<SettingsSection, string> = {
  connections: 'Connections',
  'azure-storage': 'Azure Storage',
  sessions: 'Sessions',
  snippets: 'Snippets',
  'meta-info': 'Meta Info',
  ui: 'UI',
  'keyboard-shortcuts': 'Keyboard Shortcuts',
  'ai-terminal': 'AI & Terminal',
  'ai-integrations': 'AI Integrations',
  updates: 'Updates',
  'whats-new': "What's New",
  privacy: 'Privacy',
  advanced: 'Advanced',
  spatial: 'Spatial',
  'backup-transfer': 'Backup & Transfer',
  'beta-features': 'Beta Features',
  license: 'Account',
};

/**
 * Keep the Settings dialog open when an outside-interaction (pointer-down /
 * focus / generic) actually originated inside a nested confirm/alert dialog
 * rendered at the app root (e.g. <GlobalConfirmDialog />). Radix surfaces the
 * real DOM target on `detail.originalEvent`; `event.target` is the
 * DialogContent itself.
 */
function preventIfFromAlertDialog(event: {
  detail?: { originalEvent?: Event };
  target?: EventTarget | null;
  preventDefault: () => void;
}): void {
  const domTarget =
    (event.detail?.originalEvent?.target as Element | null) ??
    (event.target as Element | null);
  if (
    domTarget?.closest?.(
      '[role="alertdialog"], [data-testid="confirm-dialog"]',
    )
  ) {
    event.preventDefault();
  }
}

export function SettingsDialog({ onAddNewConnection }: SettingsDialogProps) {
  const { track } = useAnalytics();
  // Use store for dialog state (allows external control)
  const isOpen = useSettingsDialogStore((s) => s.isOpen);
  const selectedSection = useSettingsDialogStore((s) => s.selectedSection);
  const openToSection = useSettingsDialogStore((s) => s.openToSection);
  const close = useSettingsDialogStore((s) => s.close);
  const setSelectedSection = useSettingsDialogStore((s) => s.setSelectedSection);

  const handleAddNew = () => {
    close();
    onAddNewConnection();
  };

  const renderSectionContent = () => {
    switch (selectedSection) {
      case 'connections':
        return <ConnectionsSettingsTab onAddNew={handleAddNew} />;
      case 'azure-storage':
        return <AzureStorageSettingsSection />;
      case 'sessions':
        return <SessionsSettingsTab />;
      case 'snippets':
        return <SnippetsSettingsTab />;
      case 'meta-info':
        return <MetaInfoSettingsTab />;
      case 'ui':
        return <UISettingsTab />;
      case 'keyboard-shortcuts':
        return <KeyboardShortcutsSettingsTab />;
      case 'ai-terminal':
        return <AiTerminalSettingsTab />;
      case 'ai-integrations':
        return <AiIntegrationsSettingsTab />;
      case 'updates':
        return <UpdatesSettingsTab />;
      case 'whats-new':
        return <WhatsNewSettingsTab />;
      case 'privacy':
        return <PrivacySettingsTab />;
      case 'spatial':
        return <SpatialSettingsTab />;
      case 'backup-transfer':
        return <BackupTransferSettingsTab />;
      case 'beta-features':
        return <BetaFeaturesSettingsTab />;
      case 'advanced':
        return <AdvancedSettingsTab />;
      case 'license':
        return <LicenseSettingsTab />;
      default:
        return null;
    }
  };

  return (
    <>
      {/* Trigger button outside Dialog to avoid controlled/uncontrolled state conflict */}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 active:scale-[0.96] transition-[color,box-shadow,scale] relative before:absolute before:inset-x-0 before:-inset-y-1 before:content-['']"
        title="Settings"
        data-testid="settings-button"
        onClick={() => {
          track('toolbar:settings_opened');
          openToSection('license');
        }}
      >
        <Settings className="h-4 w-4" />
      </Button>

      <Dialog
        open={isOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            close();
          }
        }}
      >
        <DialogContent
          className="sm:max-w-[900px] p-0 gap-0"
          data-testid="settings-dialog"
          // A nested confirm/alert dialog (e.g. the Meta-Info "remove
          // declaration" prompt) is rendered at the app root via
          // <GlobalConfirmDialog />, so Radix sees pointer-down / focus
          // events on it as happening *outside* the Settings dialog and
          // would dismiss Settings. Keep Settings open whenever the
          // interaction originates from such an alert dialog — users
          // expect to delete several entries in one Settings session.
          // NOTE: on Radix's outside-interaction events the real DOM
          // target lives on `detail.originalEvent`, not `e.target`
          // (which is the DialogContent node itself).
          onPointerDownOutside={preventIfFromAlertDialog}
          onFocusOutside={preventIfFromAlertDialog}
          onInteractOutside={preventIfFromAlertDialog}
        >
        <DialogHeader className="px-6 py-4 border-b border-border">
          <DialogTitle>Settings</DialogTitle>
        </DialogHeader>

        <div className="flex h-[600px]">
          <div className="py-4 pl-4 pr-2 border-r border-border bg-muted/30 overflow-y-auto">
            <SettingsMenu
              selectedSection={selectedSection}
              onSelectSection={setSelectedSection}
            />
          </div>

          <div className="flex-1 p-6 overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">{sectionTitles[selectedSection]}</h3>
            {renderSectionContent()}
          </div>
        </div>
      </DialogContent>
      </Dialog>
    </>
  );
}
